import type { EngagementType, Service } from '../../graphqlEnums'

/**
 * The backend-authored copy for a channel that can't surface media for the
 * engagement type in scope. Both strings are display text only — never switch
 * on their value.
 */
export type NoMediaChannel = {
  service: Service
  /** Short inline hint shown above the list. */
  disclaimer: string
  /** Longer explanation for the hover card, already naming its platform. */
  additionalInformation: string
}

/**
 * How the hover card explains the limitation.
 *
 * With one platform affected the backend explanation already names it, so it's
 * shown verbatim. With several, repeating a near-identical sentence per platform
 * reads badly, so the client composes one sentence and identifies the platforms
 * by their logos instead.
 */
export type NoMediaExplanation =
  | { kind: 'provided'; paragraphs: string[] }
  | { kind: 'composed'; services: Service[]; body: string }

export type NoMediaCopy = {
  hint: string
  title: string
  explanation: NoMediaExplanation
}

const TITLE_BY_ENGAGEMENT_TYPE: Record<EngagementType, string> = {
  mention: 'Images and videos in mentions',
  comment: 'Images and videos in comments',
}

/**
 * The sentence following the platform logos. Client-authored, because it has to
 * read correctly after a list of logos rather than a single platform name.
 */
const COMPOSED_BODY_BY_ENGAGEMENT_TYPE: Record<EngagementType, string> = {
  mention:
    'don’t send media with mentions to Buffer. Open the mention natively to see the full content.',
  comment:
    'don’t send media with comments to Buffer. Open the comment natively to see the full content.',
}

const distinct = <T>(values: T[]): T[] => Array.from(new Set(values))

const getExplanation = (
  channels: NoMediaChannel[],
  engagementType: EngagementType,
): NoMediaExplanation => {
  const services = distinct(channels.map(({ service }) => service))

  if (services.length > 1) {
    return {
      kind: 'composed',
      services,
      body: COMPOSED_BODY_BY_ENGAGEMENT_TYPE[engagementType],
    }
  }

  return {
    kind: 'provided',
    paragraphs: distinct(
      channels.map(({ additionalInformation }) => additionalInformation),
    ),
  }
}

/**
 * Copy for the no-media hint and its hover card, or `null` when every channel
 * in scope surfaces media.
 *
 * The hint line takes the first disclaimer: the design has room for one line,
 * and the per-platform detail lives in the hover card.
 */
export const getNoMediaCopy = (
  channels: NoMediaChannel[],
  engagementType: EngagementType,
): NoMediaCopy | null => {
  const [first] = channels
  if (!first) return null

  return {
    hint: first.disclaimer,
    title: TITLE_BY_ENGAGEMENT_TYPE[engagementType],
    explanation: getExplanation(channels, engagementType),
  }
}
